import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/SEO';
import ProductCard from './components/ProductCard';
import { products, categories } from '@/mocks/products';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const ALL_LABEL = 'All Products';

const siteUrl = import.meta.env.VITE_SITE_URL || '';

const productsJsonLd = {
  '@type': 'CollectionPage',
  name: 'Product Catalog',
  url: `${siteUrl}/products`,
  description: 'Browse high-performance veterinary solutions including growth promoters, milk boosters, and animal health supplements.',
  hasPart: products.map((p) => ({
    '@type': 'ListItem',
    item: {
      '@type': 'Product',
      name: p.name,
      url: `${siteUrl}/product/${p.id}`,
      description: p.description,
      category: p.category,
      brand: {
        '@type': 'Brand',
        name: 'Hamva Pharmaceuticals',
      },
      manufacturer: {
        '@type': 'Organization',
        name: 'Hamva Pharmaceuticals (Pvt.) Ltd.',
      },
      image: p.image,
    },
  })),
};

export default function Products() {
  const [activeCategory, setActiveCategory] = useState(ALL_LABEL);
  const { ref, isVisible } = useScrollAnimation();

  const categoryOptions = [ALL_LABEL, ...categories.map((c) => c.name)];

  const filtered =
    activeCategory === ALL_LABEL
      ? products
      : products.filter((p) => p.category.includes(activeCategory));

  return (
    <div className="min-h-screen bg-dark font-inter text-white">
      <SEO
        title="Product Catalog | Hamva Pharmaceuticals Veterinary Products"
        description="Browse high-performance veterinary solutions including growth promoters, milk boosters, liver tonics, and animal health supplements trusted by Pakistani farmers."
        keywords="veterinary products pakistan, livestock supplements, animal growth boosters, milk booster, udder development, feed additive"
        ogTitle="Product Catalog | Hamva Pharmaceuticals"
        ogDescription="Explore our complete range of veterinary solutions for livestock health and productivity."
        ogImage="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/f20d0faf8761bd92da69a0386cf62d57.png"
        canonical={`${siteUrl}/products`}
        jsonLd={productsJsonLd}
      />
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-36 pb-20 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=veterinary%20pharmaceutical%20products%20lineup%20arrangement%20dark%20studio%20dramatic%20lighting%20professional%20product%20photography%20premium%20animal%20health%20range%20dark%20background%20cinematic&width=1920&height=600&seq=prodhero&orientation=landscape"
            alt="Hamva Products"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark/80 via-dark/60 to-dark" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">Our Range</p>
          <h1 className="font-poppins font-bold text-4xl md:text-6xl text-white mb-4">
            Product Catalog
          </h1>
          <p className="text-text-muted text-lg max-w-xl leading-relaxed">
            High-performance veterinary solutions engineered for maximum livestock productivity.
          </p>
        </div>
      </section>

      {/* Section divider */}
      <div className="section-divider" />

      {/* Filter + Grid */}
      <section
        ref={ref as React.RefObject<HTMLElement>}
        className="py-16 px-4 md:px-8"
      >
        <div className="max-w-7xl mx-auto">
          {/* Category Filters */}
          <div className={`flex flex-wrap gap-3 mb-12 reveal ${isVisible ? 'visible' : ''}`}>
            {categoryOptions.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-250 cursor-pointer whitespace-nowrap border ${
                  activeCategory === cat
                    ? 'bg-accent border-accent text-white red-glow'
                    : 'border-white/10 text-text-muted hover:border-accent/40 hover:text-white'
                }`}
                style={{ background: activeCategory === cat ? undefined : '#131923' }}
              >
                {cat}
              </button>
            ))}
            <span className="ml-auto text-text-muted text-sm self-center whitespace-nowrap">
              {filtered.length} Products
            </span>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((product, i) => (
              <div
                key={product.id}
                className={`reveal reveal-delay-${(i % 4) + 1} ${isVisible ? 'visible' : ''}`}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16 py-12 rounded-2xl border border-white/5" style={{ background: '#131923' }}>
            <p className="text-text-muted text-sm mb-2">Looking for a specific product?</p>
            <h3 className="font-poppins font-bold text-2xl text-white mb-6">
              Contact Us via WhatsApp for the Full Catalog
            </h3>
            <a
              href="https://wa.me/923007321011"
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-base font-semibold text-white cursor-pointer whitespace-nowrap red-glow"
            >
              <i className="ri-whatsapp-line text-xl" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
